/**
 * PRIVA Governance — SmartEWM Signal Health (default landing).
 *
 * Live connectivity status per SmartEWM signal (Queue / Stock / Wave / Labor /
 * Exception). Read-only. Auto-refreshes every 30s. No secrets are shown.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { Badge } from "../components/ui/badge";
import { Loader2, RefreshCw, ShieldCheck, Activity } from "lucide-react";
import { getSignalHealth, extractError } from "../lib/privaAdminApi";
import ClientSystemPicker from "./ClientSystemPicker";

const SIGNAL_ORDER = ["inbound_delivery", "inbound_task", "outbound_delivery", "outbound_task"];
const SIGNAL_LABEL = {
  inbound_delivery: "Inbound Delivery",
  inbound_task: "Inbound Warehouse Task",
  outbound_delivery: "Outbound Delivery",
  outbound_task: "Outbound Warehouse Task",
};

const STATUS_BADGE = {
  Connected: "bg-emerald-100 text-emerald-700 border border-emerald-200",
  "No Data": "bg-amber-100 text-amber-700 border border-amber-200",
  Disconnected: "bg-red-100 text-red-700 border border-red-200",
  Disabled: "bg-gray-100 text-gray-600 border border-gray-200",
  Unknown: "bg-slate-100 text-slate-600 border border-slate-200",
};

function fmtTime(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
}

export default function SignalHealthPage() {
  const [ctx, setCtx] = useState({ clientId: "", clientSapId: "" });
  const [health, setHealth] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [lastChecked, setLastChecked] = useState(null);
  const timerRef = useRef(null);

  const load = useCallback(
    (showSpinner = true) => {
      if (!ctx.clientId) return;
      if (showSpinner) setLoading(true);
      setError("");
      getSignalHealth(ctx.clientId, ctx.clientSapId)
        .then((data) => {
          setHealth(data);
          setLastChecked(new Date());
        })
        .catch((e) => setError(extractError(e, "Failed to load signal health.")))
        .finally(() => setLoading(false));
    },
    [ctx.clientId, ctx.clientSapId],
  );

  useEffect(() => {
    load(true);
  }, [load]);

  // Auto-refresh every 30s (silent).
  useEffect(() => {
    timerRef.current = setInterval(() => load(false), 30000);
    return () => clearInterval(timerRef.current);
  }, [load]);

  return (
    <div className="p-6 max-w-[1280px] mx-auto" data-testid="signal-health-page">
      <header className="flex flex-wrap items-start justify-between gap-4 mb-6">
        <div>
          <div className="flex items-center gap-2 mb-1.5">
            <ShieldCheck className="w-4 h-4 text-[#025979]" />
            <p className="text-[10px] uppercase tracking-widest text-[#025979] font-semibold">
              PRIVA Governance
            </p>
          </div>
          <h1 className="text-xl font-bold text-[#111827]" style={{ fontFamily: "Chivo, sans-serif" }}>
            Signal Health
          </h1>
          <p className="text-xs text-[#6B7280] mt-1 max-w-2xl">
            Live SAP connectivity for the active SmartEWM signals. Reflects the
            latest real fetch — never mock data.
          </p>
        </div>
        <div className="flex items-end gap-3">
          <ClientSystemPicker mode="client-system" value={ctx} onChange={setCtx} testIdPrefix="signal-health" />
          <button
            type="button"
            onClick={() => load(true)}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-[#025979] border border-[#025979] rounded-sm hover:bg-[#025979]/5 transition-colors"
            data-testid="signal-health-refresh"
          >
            <RefreshCw className="w-3.5 h-3.5" />
            Refresh
          </button>
        </div>
      </header>

      <div className="flex items-center gap-2 mb-3 text-[11px] text-[#6B7280]" data-testid="signal-health-heartbeat">
        <Activity className="w-3 h-3 text-emerald-500" />
        Last checked: <span className="font-mono">{lastChecked ? lastChecked.toLocaleTimeString() : "—"}</span>
        <span className="text-[#9CA3AF]">· auto-refresh 30s</span>
        {health?.liveSapRequired ? (
          <Badge className="ml-1 bg-[#E0F2FE] text-[#025979] border border-[#025979]/20">Live SAP required</Badge>
        ) : null}
      </div>

      {error ? (
        <div className="text-red-600 text-xs py-3" data-testid="signal-health-error">{error}</div>
      ) : null}

      <div className="bg-white border border-[#E5E7EB] rounded-sm shadow-sm overflow-x-auto">
        <table className="min-w-full text-xs" data-testid="signal-health-table">
          <thead className="bg-[#F8FAFC] text-[10px] uppercase tracking-wider text-[#6B7280]">
            <tr>
              <Th>Signal</Th>
              <Th>Status</Th>
              <Th>Source</Th>
              <Th>Connection</Th>
              <Th>Last Successful Fetch</Th>
              <Th>Latency</Th>
              <Th>Rows</Th>
              <Th>Last Error</Th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[#E5E7EB]">
            {loading && !health ? (
              <tr><td colSpan={8} className="text-center py-12 text-[#6B7280]"><Loader2 className="w-4 h-4 mx-auto animate-spin" /></td></tr>
            ) : (
              SIGNAL_ORDER.map((key) => {
                const s = (health && health[key]) || { enabled: false, status: "Disabled" };
                return (
                  <tr key={key} className="hover:bg-[#F8FAFC]/70" data-testid={`signal-health-row-${key}`}>
                    <Td className="font-medium text-[#111827]">{SIGNAL_LABEL[key]}</Td>
                    <Td>
                      <Badge
                        className={STATUS_BADGE[s.status] || STATUS_BADGE.Unknown}
                        data-testid={`signal-health-status-${key}`}
                      >
                        {s.status || "Unknown"}
                      </Badge>
                    </Td>
                    <Td className="font-mono text-[11px] text-[#374151]">{s.source || "—"}</Td>
                    <Td className="text-[#374151]">{s.connectionName || "—"}</Td>
                    <Td className="font-mono text-[11px] text-[#374151] whitespace-nowrap">{fmtTime(s.lastSuccessfulFetch)}</Td>
                    <Td className="font-mono text-[11px] text-[#374151]">{s.lastLatencyMs != null ? `${s.lastLatencyMs} ms` : "—"}</Td>
                    <Td className="font-mono text-[11px] text-[#374151]">{s.lastRowCount != null ? s.lastRowCount : "—"}</Td>
                    <Td className="text-[11px] text-red-600 max-w-[280px]">{s.lastError || <span className="text-[#9CA3AF]">—</span>}</Td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Th({ children }) {
  return <th className="text-left font-medium px-3 py-2 whitespace-nowrap">{children}</th>;
}
function Td({ children, className = "" }) {
  return <td className={`px-3 py-2 align-top ${className}`}>{children}</td>;
}
